import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import {
  ShoppingBag, Download, Package, Boxes, Star, ShoppingCart,
  User, LogIn, LogOut, Image, ChevronRight, Search, X, Menu,
  History, Mail, Phone, Calendar, Send, Textarea as TextareaIcon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_CONFIG = {
  digital_products: { label: "Digital Products", icon: Download, gradient: "from-violet-500 to-purple-600", bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  merchandise: { label: "Branded Merchandise", icon: Package, gradient: "from-amber-500 to-orange-600", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  business_kits: { label: "Business Tool Kits", icon: Boxes, gradient: "from-blue-600 to-royal-blue-700", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" }
};

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [customer, setCustomer] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Dialog states
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [myOrders, setMyOrders] = useState([]);

  // Auth form
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [authError, setAuthError] = useState("");

  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState({ quantity: 1, customer_name: "", customer_email: "" });
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Inline contact form
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`, { params: { is_active: true } });
      setProducts(res.data);
    } catch (err) { console.error("Failed to fetch products:", err); }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCustomer(res.data);
    } catch { setCustomer(null); }
    setAuthChecked(true);
  }, []);

  useEffect(() => { fetchProducts(); checkAuth(); }, [fetchProducts, checkAuth]);
  useEffect(() => { document.title = "Store | Opulence Logistics Management"; }, []);

  const filteredProducts = products.filter(p => {
    const matchesCategory = activeCategory === "all" || p.category === activeCategory;
    const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredProducts = products.filter(p => p.is_featured);

  const handleAuth = async () => {
    setAuthError("");
    try {
      if (authMode === "register") {
        const res = await axios.post(`${API}/auth/register`, authForm, { withCredentials: true });
        setCustomer(res.data);
      } else {
        const res = await axios.post(`${API}/auth/login`, { email: authForm.email, password: authForm.password }, { withCredentials: true });
        setCustomer(res.data);
      }
      setAuthDialogOpen(false);
      setAuthForm({ name: "", email: "", password: "", phone: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAuthError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(". ") : "Something went wrong");
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setCustomer(null);
  };

  const openCheckout = (product) => {
    setSelectedProduct(product);
    setCheckoutForm({
      quantity: 1,
      customer_name: customer?.name || "",
      customer_email: customer?.email || ""
    });
    setCheckoutDialogOpen(true);
  };

  const handleCheckout = async () => {
    if (!selectedProduct) return;
    setCheckoutLoading(true);
    try {
      const payload = {
        product_id: selectedProduct.id,
        quantity: checkoutForm.quantity,
        customer_email: checkoutForm.customer_email,
        customer_name: checkoutForm.customer_name
      };
      const res = await axios.post(`${API}/store/checkout`, payload);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const fetchMyOrders = async () => {
    try {
      const res = await axios.get(`${API}/auth/orders`, { withCredentials: true });
      setMyOrders(res.data);
    } catch { setMyOrders([]); }
    setOrdersDialogOpen(true);
  };

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;
    setContactSubmitting(true);
    try {
      await axios.post(`${API}/leads/contact`, { ...contactForm, source: "storefront_contact" });
      setContactSubmitted(true);
    } catch { /* silent */ }
    setContactSubmitting(false);
  };

  const ProductCard = ({ product, featured = false }) => {
    const cat = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.merchandise;
    const CatIcon = cat.icon;
    const discount = product.compare_price ? Math.round((1 - product.price / product.compare_price) * 100) : 0;

    return (
      <Card className={`group hover:shadow-2xl transition-all duration-300 overflow-hidden border-slate-200 hover:border-slate-300 ${featured ? 'ring-2 ring-yellow-400/50' : ''}`} data-testid={`storefront-product-${product.id}`}>
        <div className={`${featured ? 'h-56' : 'h-48'} ${product.image_url ? '' : `bg-gradient-to-br ${cat.gradient}`} relative flex items-center justify-center overflow-hidden`}>
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <>
              <div className="text-white/10 absolute inset-0 flex items-center justify-center">
                <CatIcon size={100} strokeWidth={0.5} />
              </div>
              <div className="relative z-10 text-center px-6">
                <Image size={28} className="text-white/60 mx-auto mb-2" />
                <p className="text-white/70 text-xs font-medium leading-tight max-w-[200px]">{product.image_placeholder || "Product Image"}</p>
              </div>
            </>
          )}
          {product.is_featured && (
            <Badge className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 shadow-lg text-xs">
              <Star size={10} className="mr-1" /> Featured
            </Badge>
          )}
          {discount > 0 && (
            <Badge className="absolute top-3 right-3 bg-red-500 text-white shadow-lg text-xs">
              {discount}% OFF
            </Badge>
          )}
          <Badge className={`absolute bottom-3 left-3 ${cat.bg} ${cat.text} ${cat.border} text-xs`}>
            {product.product_type === 'digital' ? 'Instant Download' : 'Ships in 3-5 days'}
          </Badge>
        </div>
        <CardContent className="p-5">
          <Badge variant="outline" className={`text-xs mb-2 ${cat.text} ${cat.border}`}>{cat.label}</Badge>
          <h3 className="font-cabinet font-bold text-lg text-slate-900 mb-1 group-hover:text-royal-blue-700 transition-colors leading-tight">{product.name}</h3>
          <p className="text-sm text-slate-500 line-clamp-2 mb-4 min-h-[40px]">{product.description}</p>
          {product.metadata && Object.keys(product.metadata).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {Object.entries(product.metadata).slice(0, 3).map(([k, v]) => (
                <span key={k} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">{k}: {v}</span>
              ))}
            </div>
          )}
          <Separator className="mb-3" />
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-cabinet text-2xl font-bold text-slate-900">{formatCurrency(product.price)}</span>
              {product.compare_price && (
                <span className="text-sm text-slate-400 line-through">{formatCurrency(product.compare_price)}</span>
              )}
            </div>
            <Button onClick={() => openCheckout(product)} className="bg-royal-blue-700 hover:bg-royal-blue-800 shadow-lg shadow-royal-blue-700/20" data-testid={`storefront-buy-${product.id}`}>
              <ShoppingCart size={16} className="mr-1" /> Buy Now
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="storefront-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-royal-blue-600 to-royal-blue-800 flex items-center justify-center shadow-lg shadow-royal-blue-600/30">
              <span className="text-white font-bold text-lg">O</span>
            </div>
            <div>
              <h1 className="font-cabinet text-xl font-bold tracking-tight text-slate-900">OpLogMan</h1>
              <p className="text-[10px] text-slate-400 font-manrope -mt-0.5">Opulence Logistics Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/contact">
              <Button variant="ghost" size="sm" className="hidden md:flex"><Mail size={16} className="mr-1" /> Contact</Button>
            </Link>
            {authChecked && customer ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={fetchMyOrders} data-testid="my-orders-btn">
                  <History size={16} className="mr-1" /> My Orders
                </Button>
                <span className="text-sm text-slate-600 hidden md:inline">Hi, {customer.name}</span>
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                  <LogOut size={16} />
                </Button>
              </div>
            ) : authChecked ? (
              <Button variant="outline" size="sm" onClick={() => { setAuthMode("login"); setAuthDialogOpen(true); }} data-testid="login-btn">
                <LogIn size={16} className="mr-1" /> Sign In
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-royal-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Badge className="bg-royal-blue-600/30 text-royal-blue-300 border-royal-blue-500/30 mb-4">Official Store</Badge>
          <h2 className="font-cabinet text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
            Premium Business Tools & Merchandise
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Digital toolkits, branded merchandise, and executive business kits from Opulence Logistics Management Corp.
          </p>
          <div className="max-w-md mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-11 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/20 h-12 rounded-xl"
              data-testid="storefront-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <div className="max-w-7xl mx-auto px-6 -mt-6 relative z-10">
        <div className="flex gap-3 justify-center flex-wrap">
          <Button
            variant={activeCategory === "all" ? "default" : "outline"}
            onClick={() => setActiveCategory("all")}
            className={`rounded-full shadow-md ${activeCategory === "all" ? "bg-royal-blue-700 hover:bg-royal-blue-800" : "bg-white hover:bg-slate-50"}`}
            data-testid="storefront-filter-all"
          >
            <ShoppingBag size={16} className="mr-2" /> All Products ({products.length})
          </Button>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
            const count = products.filter(p => p.category === key).length;
            const Icon = config.icon;
            return (
              <Button
                key={key}
                variant={activeCategory === key ? "default" : "outline"}
                onClick={() => setActiveCategory(key)}
                className={`rounded-full shadow-md ${activeCategory === key ? "bg-royal-blue-700 hover:bg-royal-blue-800" : "bg-white hover:bg-slate-50"}`}
                data-testid={`storefront-filter-${key}`}
              >
                <Icon size={16} className="mr-2" /> {config.label} ({count})
              </Button>
            );
          })}
        </div>
      </div>

      {/* Featured Products Highlight */}
      {activeCategory === "all" && !searchQuery && featuredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 mt-10">
          <div className="flex items-center gap-2 mb-6">
            <Star size={20} className="text-yellow-500" />
            <h3 className="font-cabinet text-2xl font-bold text-slate-900">Featured Products</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProducts.slice(0, 3).map(p => <ProductCard key={p.id} product={p} featured />)}
          </div>
        </section>
      )}

      {/* Product Grid */}
      <section className="max-w-7xl mx-auto px-6 mt-10 pb-16">
        {activeCategory !== "all" || searchQuery ? (
          <h3 className="font-cabinet text-2xl font-bold text-slate-900 mb-6">
            {searchQuery ? `Search Results (${filteredProducts.length})` : CATEGORY_CONFIG[activeCategory]?.label || "All Products"}
          </h3>
        ) : (
          <h3 className="font-cabinet text-2xl font-bold text-slate-900 mb-6">All Products</h3>
        )}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-medium">No products found</p>
            <p className="text-sm text-slate-400 mt-1">Try a different search or category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Contact & Booking Section */}
      <section className="bg-white border-t border-slate-200 py-16" data-testid="storefront-contact-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h3 className="font-cabinet text-3xl font-bold text-slate-900 mb-2">Ready to Get Started?</h3>
            <p className="text-slate-500 max-w-xl mx-auto">Book a consultation or send us a message. We'd love to hear from you.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Quick Contact Form */}
            <div>
              <h4 className="font-cabinet text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Send size={18} className="text-royal-blue-700" /> Quick Message
              </h4>
              {contactSubmitted ? (
                <div className="p-8 bg-green-50 border border-green-200 rounded-xl text-center" data-testid="storefront-contact-success">
                  <p className="text-green-700 font-medium">Thank you! We'll be in touch soon.</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => { setContactSubmitted(false); setContactForm({ name: "", email: "", message: "" }); }}>Send Another</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input data-testid="storefront-contact-name" placeholder="Your Name *" value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} />
                    <Input data-testid="storefront-contact-email" type="email" placeholder="Your Email *" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} />
                  </div>
                  <Textarea data-testid="storefront-contact-message" placeholder="How can we help? *" rows={4} value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} />
                  <Button onClick={handleContactSubmit} disabled={contactSubmitting} className="w-full bg-royal-blue-700 hover:bg-royal-blue-800" data-testid="storefront-contact-submit">
                    {contactSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              )}
              <Link to="/contact" className="inline-flex items-center gap-1 text-sm text-royal-blue-700 hover:underline mt-4">
                Full contact page with booking <ChevronRight size={14} />
              </Link>
            </div>
            {/* Booking CTA */}
            <div>
              <h4 className="font-cabinet text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar size={18} className="text-royal-blue-700" /> Book a Consultation
              </h4>
              <Card className="border-royal-blue-200 overflow-hidden">
                <CardContent className="p-0">
                  <iframe
                    src={process.env.REACT_APP_BOOKING_URL || ""}
                    title="Book a consultation"
                    className="w-full border-0"
                    style={{ height: '420px' }}
                    data-testid="storefront-booking-iframe"
                    allow="fullscreen"
                  />
                </CardContent>
              </Card>
              <p className="text-xs text-slate-400 text-center mt-2">Powered by Microsoft 365 Bookings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-royal-blue-500 to-royal-blue-700 flex items-center justify-center">
                  <span className="text-white font-bold">O</span>
                </div>
                <div>
                  <h4 className="font-cabinet font-bold">OpLogMan</h4>
                  <p className="text-xs text-slate-400">Opulence Logistics Management Corp.</p>
                </div>
              </div>
              <p className="text-sm text-slate-400">Premium business tools, merchandise, and digital resources for logistics professionals.</p>
            </div>
            <div>
              <h4 className="font-cabinet font-semibold mb-3">Categories</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => setActiveCategory("digital_products")}>Digital Products</li>
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => setActiveCategory("merchandise")}>Branded Merchandise</li>
                <li className="hover:text-white cursor-pointer transition-colors" onClick={() => setActiveCategory("business_kits")}>Business Tool Kits</li>
              </ul>
            </div>
            <div>
              <h4 className="font-cabinet font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2"><Mail size={14} /> info@oplogman.com</li>
                <li className="flex items-center gap-2"><Phone size={14} /> 281-938-9601</li>
                <li><Link to="/contact" className="flex items-center gap-2 hover:text-white transition-colors"><Calendar size={14} /> Book a Consultation</Link></li>
              </ul>
            </div>
          </div>
          <Separator className="my-8 bg-slate-800" />
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} Opulence Logistics Management Corp. All rights reserved.</p>
            <div className="flex gap-6 text-sm text-slate-400">
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy & Terms</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={authDialogOpen} onOpenChange={setAuthDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl">{authMode === "login" ? "Welcome Back" : "Create Account"}</DialogTitle>
            <DialogDescription>
              {authMode === "login" ? "Sign in to track your orders" : "Create an account to track orders and get updates"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {authError && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{authError}</div>}
            {authMode === "register" && (
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input data-testid="auth-name" value={authForm.name} onChange={e => setAuthForm({...authForm, name: e.target.value})} placeholder="John Smith" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="auth-email" type="email" value={authForm.email} onChange={e => setAuthForm({...authForm, email: e.target.value})} placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input data-testid="auth-password" type="password" value={authForm.password} onChange={e => setAuthForm({...authForm, password: e.target.value})} placeholder="Enter password" />
            </div>
            {authMode === "register" && (
              <div className="space-y-2">
                <Label>Phone (optional)</Label>
                <Input value={authForm.phone} onChange={e => setAuthForm({...authForm, phone: e.target.value})} placeholder="+1 555-0123" />
              </div>
            )}
          </div>
          <DialogFooter className="flex-col gap-3">
            <Button onClick={handleAuth} className="w-full bg-royal-blue-700 hover:bg-royal-blue-800" data-testid="auth-submit-btn">
              {authMode === "login" ? "Sign In" : "Create Account"}
            </Button>
            <p className="text-center text-sm text-slate-500">
              {authMode === "login" ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => { setAuthMode(authMode === "login" ? "register" : "login"); setAuthError(""); }}
                className="text-royal-blue-700 hover:underline font-medium"
                data-testid="auth-toggle-mode"
              >
                {authMode === "login" ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet">Complete Your Purchase</DialogTitle>
            <DialogDescription>You'll be redirected to Stripe for secure payment</DialogDescription>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4 py-2">
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-cabinet font-bold">{selectedProduct.name}</p>
                    <Badge variant="outline" className="text-xs mt-1">{CATEGORY_CONFIG[selectedProduct.category]?.label}</Badge>
                  </div>
                  <span className="font-cabinet text-xl font-bold">{formatCurrency(selectedProduct.price)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" min="1" max={selectedProduct.product_type === 'physical' ? selectedProduct.inventory_count : 99}
                  value={checkoutForm.quantity}
                  onChange={e => setCheckoutForm({...checkoutForm, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
              {!customer && (
                <>
                  <div className="space-y-2">
                    <Label>Your Name *</Label>
                    <Input data-testid="checkout-name" value={checkoutForm.customer_name}
                      onChange={e => setCheckoutForm({...checkoutForm, customer_name: e.target.value})}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Your Email *</Label>
                    <Input data-testid="checkout-email" type="email" value={checkoutForm.customer_email}
                      onChange={e => setCheckoutForm({...checkoutForm, customer_email: e.target.value})}
                      placeholder="you@example.com"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    <button onClick={() => { setCheckoutDialogOpen(false); setAuthMode("register"); setAuthDialogOpen(true); }} className="text-royal-blue-700 hover:underline">
                      Create an account
                    </button> to track your orders
                  </p>
                </>
              )}
              <Separator />
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-600">Total</span>
                <span className="font-cabinet text-2xl font-bold text-royal-blue-700">{formatCurrency(selectedProduct.price * checkoutForm.quantity)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCheckout} disabled={checkoutLoading}
              className="bg-green-600 hover:bg-green-700" data-testid="storefront-checkout-btn">
              {checkoutLoading ? "Processing..." : "Proceed to Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Orders Dialog */}
      <Dialog open={ordersDialogOpen} onOpenChange={setOrdersDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cabinet">My Orders</DialogTitle>
            <DialogDescription>Track your purchase history</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {myOrders.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingBag size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No orders yet</p>
              </div>
            ) : (
              myOrders.map(order => (
                <div key={order.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-mono text-sm text-slate-500">{order.order_number}</p>
                      <p className="font-medium">{order.product_name}</p>
                      <p className="text-sm text-slate-500">Qty: {order.quantity} &middot; {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-cabinet font-bold">{formatCurrency(order.total)}</p>
                      <Badge className={
                        order.status === 'paid' ? 'bg-green-100 text-green-600' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-slate-100 text-slate-600'
                      }>{order.status}</Badge>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
